import React, { useEffect, useRef, useState } from 'react';
import { useSpring, animated } from 'react-spring';
import { FaRobot, FaUser } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import '../css/home.css';


const Hero = () => {
  // Ref for animation element
  const heroRef = useRef(null);
  
  // State for speech bubbles
  const [bubbles, setBubbles] = useState([]);
  
  // Calculate bubble size based on sentence length
  const getBubbleSize = (sentence) => {
    const wordCount = sentence.split(' ').length;
    
    if (wordCount <= 2) {
      return Math.random() * 20 + 70; // 70-90px
    } else if (wordCount <= 4) {
      return Math.random() * 30 + 90; // 90-120px
    } else {
      return Math.random() * 40 + 120; // 120-160px
    }
  };
  
  // Generate random speech bubbles
  useEffect(() => {
    const generateBubbles = () => {
      const newBubbles = [];
      const occupiedAreas = []; 
      
      // Improved check overlap function for better spacing
      const checkOverlap = (checkLeft, checkTop, checkSize) => {
        return occupiedAreas.some(area => {
          const distance = Math.sqrt(
            Math.pow(area.left - checkLeft, 2) + 
            Math.pow(area.top - checkTop, 2)
          );
          // Increased minimum distance factor from 40 to 60
          const minDistance = (checkSize/100 + area.size/100) * 60;
          return distance < minDistance;
        });
      };
      
      // Increase grid sections for better distribution
      const gridSections = 6; // Changed from 4 to 6 for finer control
      const usedSections = new Set();
      
      // Improved bubble positioning parameters
      const horizontalPadding = 5; // Reduced from 10 to use more screen space
      const verticalPadding = 5;   // Reduced from 15 to use more screen space
      const horizontalRange = 90;  // Increased from 80 to use more screen space
      const verticalRange = 90;    // Increased from 70 to use more screen space
      
      // Generate 15 random bubbles instead of 12
      for (let i = 0; i < 30; i++) {
        const sentence = bubbleSentences[Math.floor(Math.random() * bubbleSentences.length)];
        const size = getBubbleSize(sentence);
        
        let left = 0;
        let top = 0;
        let overlap = true;
        let attempts = 0;
        
        // Try to find a non-overlapping position - increased max attempts
        while (overlap && attempts < 40) {
          if (usedSections.size < gridSections * gridSections) {
            let sectionX, sectionY;
            let sectionFound = false;
            
            // Try to find an unused section
            while (!sectionFound && attempts < 15) {
              sectionX = Math.floor(Math.random() * gridSections);
              sectionY = Math.floor(Math.random() * gridSections);
              const sectionKey = `${sectionX}-${sectionY}`;
              
              if (!usedSections.has(sectionKey)) {
                sectionFound = true;
                usedSections.add(sectionKey);
                
                // Calculate position within this section with more edge coverage
                const sectionWidth = horizontalRange / gridSections;
                const sectionHeight = verticalRange / gridSections;
                
                left = horizontalPadding + sectionX * sectionWidth + Math.random() * (sectionWidth * 0.7);
                top = verticalPadding + sectionY * sectionHeight + Math.random() * (sectionHeight * 0.7);
              }
              attempts++;
            }
            
            if (!sectionFound) {
              left = Math.random() * horizontalRange + horizontalPadding;
              top = Math.random() * verticalRange + verticalPadding;
            }
          } else {
            left = Math.random() * horizontalRange + horizontalPadding;
            top = Math.random() * verticalRange + verticalPadding;
          }
          
          // Check if this position overlaps with any existing bubble
          overlap = checkOverlap(left, top, size);
          attempts++;
        }
        
        // Add to occupied areas even if overlapping (best effort)
        occupiedAreas.push({ left, top, size });
        
        // Random tail position for speech bubble effect
        const tailPosition = Math.floor(Math.random() * 4); // 0: bottom, 1: right, 2: top, 3: left
        
        newBubbles.push({
          id: uuidv4(),
          sentence: sentence,
          size: size,
          left: left,
          top: top,
          delay: Math.random() * 3,
          duration: Math.random() * 6 + 6,
          opacity: Math.random() * 0.1 + 0.3, // Slightly increased opacity
          isViolet: Math.random() > 0.5,
          tailPosition: tailPosition // New property for tail position
        });
      }
      
      setBubbles(newBubbles);
    };

    const generateBubbles2 = () => {
        const newBubbles = [];
        
        // Create a more defined grid with more sections
        const gridColumns = 8;
        const gridRows = 6;
        
        // Track which grid cells are already used
        const usedCells = new Set();
        
        // Total number of bubbles to generate (less than total grid cells to avoid overcrowding)
        const totalBubbles = Math.min(35, gridColumns * gridRows - 5);
        
        for (let i = 0; i < totalBubbles; i++) {
          // Select random sentence
          const sentence = bubbleSentences[Math.floor(Math.random() * bubbleSentences.length)];
          
          // Calculate size based on sentence length
          const size = getBubbleSize(sentence);
          
          // Find an unused grid cell
          let cellX, cellY;
          let cellFound = false;
          let attempts = 0;
          
          while (!cellFound && attempts < 50) {
            cellX = Math.floor(Math.random() * gridColumns);
            cellY = Math.floor(Math.random() * gridRows);
            const cellKey = `${cellX}-${cellY}`;
            
            if (!usedCells.has(cellKey)) {
              cellFound = true;
              usedCells.add(cellKey);
            }
            
            attempts++;
          }
          
          // If we couldn't find an unused cell, skip this bubble
          if (!cellFound) continue;
          
          // Calculate the center position of the cell
          // Add small random offset (±10%) to avoid perfect grid alignment
          const offsetX = (Math.random() * 0.2) - 0.1; // -10% to +10%
          const offsetY = (Math.random() * 0.2) - 0.1; // -10% to +10%
          
          const cellWidth = 100 / gridColumns;
          const cellHeight = 100 / gridRows;
          
          // Calculate position at center of cell plus small random offset
          const left = (cellX * cellWidth) + (cellWidth / 2) + (offsetX * cellWidth);
          const top = (cellY * cellHeight) + (cellHeight / 2) + (offsetY * cellHeight);
          
          // Random tail position for speech bubble effect
          const tailPosition = Math.floor(Math.random() * 4); // 0: bottom, 1: right, 2: top, 3: left
          
          newBubbles.push({
            id: uuidv4(),
            sentence: sentence,
            size: size,
            left: left,
            top: top,
            delay: Math.random() * 3,
            duration: Math.random() * 6 + 6,
            opacity: Math.random() * 0.1 + 0.3,
            isViolet: Math.random() > 0.5,
            tailPosition: tailPosition
          });
        }
        
        setBubbles(newBubbles);
      };
    
    // generateBubbles();
    generateBubbles2();
  }, []);
  
  // Animation on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);
    
    // Store current ref value to avoid issues in cleanup
    const currentRef = heroRef.current;
    
    // Observe elements
    if (currentRef) observer.observe(currentRef);
    
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  // Spring animation for main elements
  const heroSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(30px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
    config: { tension: 120, friction: 14 },
    delay: 300,
  });
  
  const imageSpring = useSpring({
    from: { opacity: 0, transform: 'scale(0.9)' },
    to: { opacity: 1, transform: 'scale(1)' },
    config: { tension: 120, friction: 12 },
    delay: 600,
  });
  
  const buttonSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
    config: { tension: 120, friction: 14 },
    delay: 900,
  });

  return (
    <section ref={heroRef} className="hero-section">
      <div className="hero-grid-background"></div>
      
      {/* Random floating speech bubbles */}
      <div className="hero-bubbles">
        {bubbles.map(bubble => (
            <div
            key={bubble.id}
            className={`floating-bubble ${bubble.isViolet ? 'bubble-violet' : 'bubble-yellow'} tail-${bubble.tailPosition}`}
            style={{
                width: `${bubble.size}px`,
                height: `auto`,
                left: `${bubble.left}%`,
                top: `${bubble.top}%`,
                animationDelay: `${bubble.delay}s`,
                animationDuration: `${bubble.duration}s`,
                opacity: bubble.opacity,
                zIndex: Math.floor(Math.random() * 5) + 1, // Add random z-index for layering
            }}
            >
            <span className="bubble-text">{bubble.sentence}</span>
            </div>
        ))}
      </div>
      
      <div className="container">
        <div className="hero-content">
          <animated.div style={heroSpring} className="hero-text-center">
            <h1>Master English Through <span className="highlight">AI Conversations</span></h1>
            <p>
              Improve your English speaking skills naturally with our AI-powered 
              conversation partner. Practice anytime, anywhere, and 
              receive personalized feedback.
            </p>
          </animated.div>
          
          <animated.div style={imageSpring} className="hero-conversation">
            <div className="conversation-visual">
              <div className="user-side">
                <div className="avatar-circle">
                  <FaUser size={24} />
                </div>
                <div className="speech-bubble user-bubble">
                  <p>How do I ask for directions?</p>
                </div>
              </div>
              
              <div className="ai-side">
                <div className="avatar-circle ai-avatar">
                  <FaRobot size={24} />
                </div>
                <div className="speech-bubble ai-bubble">
                  <p>You can say "Excuse me, how do I get to the nearest station?"</p>
                </div>
              </div>
            </div>
          </animated.div>
          
          {/* <animated.div style={buttonSpring} className="cta-container">
            <button className="cta-button">Start Speaking Now <span className="arrow">→</span></button>
          </animated.div> */}
        </div>
      </div>
    </section>
  );
};

export default Hero;



// List of sentences for bubbles
const bubbleSentences = [
    // 1-2 words
    "Hello!",
    "Thank you!",
    "Excuse me?",
    "Try again.",
    "Well done!",
    "Good job!",
    "How's it?",
    "Perfect pronunciation!",
    "Nice work!",
    "Amazing!",
    "Got it?",
    "Keep going!",
    "Not bad!",
    "Awesome!",
    "Oops!",
    "Relax.",
    "Sure!",
    "Okay.",
    "Goodbye!",
    "Welcome!",
    "Wow!",
    "Indeed!",
    "Almost!",
    "Fantastic!",
    "Correct!",
  
    // 3-4 words
    "How are you?",
    "Nice to meet you.",
    "What's your name?",
    "Where is the classroom?",
    "I don't understand.",
    "Can you help me?",
    "Let's practice together.",
    "Speak more slowly please.",
    "How do you say...?",
    "That sounds great!",
    "What does this mean?",
    "Is everything all right?",
    "Please repeat after me.",
    "Can I ask something?",
    "What’s your favorite food?",
    "I’m feeling much better.",
    "Do you speak English?",
    "Have a great day!",
    "When does class start?",
    "Where are you going?",
    "What’s happening here?",
    "Can I sit here?",
    "How can I help?",
    "Is that your book?",
    "Do you need anything?",
  
    // 5-7 words
    "Could you repeat that, please?",
    "How do I get to the station?",
    "I'd like to order some coffee.",
    "What time does the store open?",
    "Can you recommend a good restaurant?",
    "I'm learning English with AI assistance.",
    "My pronunciation is getting much better!",
    "Would you mind speaking more slowly?",
    "Let me try to say that again.",
    "I've been studying for three months.",
    "This app is helping me improve quickly.",
    "Could you explain that once again?",
    "How can I improve my pronunciation?",
    "Do you know where the library is?",
    "Would you like to practice with me?",
    "I'm having trouble understanding this.",
    "What should I say in response?",
    "May I ask you a question?",
    "Is this the right way to pronounce?",
    "Could you tell me the meaning?",
    "How long have you lived here?",
    "Have you been to this place?",
    "Please correct me if I'm wrong.",
    "How do I improve my vocabulary?",
    "Is there a better way to say this?"
  ];
  
